import { GeneralPaginationProps, PageSizeOption, PageSizeValue } from "@/types/Pagination";
import type { ComponentType } from "react";
import ReactPaginate, { type ReactPaginateProps } from "react-paginate";

const ReactPaginateComponent = ((ReactPaginate as unknown as { default?: ComponentType<ReactPaginateProps> }).default ??
	ReactPaginate) as ComponentType<ReactPaginateProps>;

const DEFAULT_PAGE_SIZES: PageSizeOption[] = [
	{ label: "20", value: 20 },
	{ label: "30", value: 30 },
	{ label: "40", value: 40 },
	{ label: "50", value: 50 },
	{ label: "100", value: 100 },
	{ label: "150", value: 150 },
	{ label: "200", value: 200 },
	{ label: "ALL", value: "ALL" },
];

const DEFAULT_PAGE_SIZE: PageSizeValue = 100;

const Paginator: {
	pageSizes: PageSizeOption[];
	defaultPageSize: PageSizeValue;
} = {
	pageSizes: DEFAULT_PAGE_SIZES,
	defaultPageSize: DEFAULT_PAGE_SIZE,
};

export default function GeneralPagination({
	pagination,
	onPageChange,
	onPageSizeChange,
	isFetching = false,
	pageSizeOptions = DEFAULT_PAGE_SIZES, // Default if not provided
	defaultPageSize = DEFAULT_PAGE_SIZE, // Default if not provided
}: GeneralPaginationProps) {
	const nextIcon = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			stroke="currentColor"
			className="text-primary h-5 w-5"
		>
			<path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
		</svg>
	);

	const previousIcon = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			stroke="currentColor"
			className="text-primary h-5 w-5"
		>
			<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
		</svg>
	);

	const handlePageClick = (event: { selected: number }) => {
		onPageChange(event.selected + 1);
	};

	return (
		<>
			<div className="flex flex-col gap-2">
				<ReactPaginateComponent
					disableInitialCallback={isFetching}
					className="flex items-center justify-between"
					breakLabel="..."
					nextLabel={nextIcon}
					pageLinkClassName={isFetching ? "cursor-wait" : ""}
					previousLabel={previousIcon}
					onPageChange={handlePageClick}
					pageRangeDisplayed={5}
					pageCount={pagination.totalPages}
					forcePage={pagination.activePage - 1}
					renderOnZeroPageCount={null}
					pageClassName="mx-2 border-b border-transparent text-black p-1"
					activeClassName="text-primary border-b-primary"
					previousClassName="border-b border-transparent"
					nextClassName="border-b border-transparent"
				/>
			</div>

			<div className="flex items-center">
				<select
					id="pageSize"
					value={pagination.perPage ?? defaultPageSize}
					onChange={e => onPageSizeChange(e.target.value as PageSizeValue)}
					className="w-20 border p-1 outline-gray-200"
				>
					{pageSizeOptions.map(pageSize => (
						<option
							key={pageSize.label}
							value={pageSize.value === "ALL" && pagination.totalRecords > 0 ? pagination.totalRecords : pageSize.value}
						>
							{pageSize.label}
						</option>
					))}
				</select>
			</div>
		</>
	);
}

export { Paginator };
